export const SITE_WALKTHROUGH = {
  title: "Client Website Walkthrough",
  subtitle:
    "A file-by-file tour showing what the code builds and what it communicates to clients.",

  overviewBullets: [
    {
      id: "frame",
      label: "The Frame",
      text: "How we position you.",
      color: "#6366f1",
    }, // Indigo
    {
      id: "proof",
      label: "The Proof",
      text: "How we earn trust.",
      color: "#22c55e",
    }, // Green
    {
      id: "flow",
      label: "The Flow",
      text: "How we convert.",
      color: "#f59e0b",
    }, // Amber
  ],

  groups: [
    {
      id: "shell",
      label: "App Shell",
      description: "Navigation, login state, modals, and the frame.",
      steps: [
        {
          id: "app",
          file: "src/App.js",
          lens: ["frame", "flow"],
          houseStage: "Planning",
          scottIntent: "Give a clear starting point. No clutter.",
          renders:
            "Global nav + routing logic + modals + footer system status.",
          messagingIntent: [
            "Own the frame: simple nav, high signal, no clutter.",
            "Prizing: client portal + screening language.",
            "Low-friction conversion: Book Now opens contact flow immediately.",
          ],
          whatClientFeels:
            "This feels organized. I'm not being sold; I'm entering a system.",
          talkTrack:
            "We keep it simple. The site works like the program—everything has a place, and we don't guess.",
          uiCues: [
            "Fixed nav = control + stability.",
            "START HERE / BUILD SEQUENCE / PROOF navigation labels.",
            "Cmd/Ctrl+K (Why) = operator shortcut.",
          ],
          keyCopy: {
            ctas: ["Book Now", "Why this works", "Client Portal"],
            systemLanguage: ["SYSTEM ONLINE", "SYSTEM STATUS: OPTIMAL"],
          },
          riskIfMissing:
            "Without this structure, the experience becomes 'a page' instead of 'a system' — trust drops immediately.",
          successCriteria: [
            "Nav is fixed + stable across views.",
            "Modals sit above everything (z-index) and close cleanly.",
            "Keyboard shortcut toggles Why modal.",
          ],
          actions: [
            { type: "jump", label: "Preview Home", target: "home" },
            { type: "jump", label: "Preview Plan", target: "plan" },
            { type: "modal", label: "Test Why Modal", target: "why" },
          ],
        },
      ],
    },

    {
      id: "views",
      label: "Views (Pages)",
      description: "Home, Plan, Results, and Client portal.",
      steps: [
        {
          id: "home",
          file: "src/views/HomeView.jsx",
          lens: ["frame", "proof", "flow"],
          houseStage: "Clear Land",
          scottIntent:
            "Cut through the noise. Show them the House metaphor immediately.",
          renders: "Hero frame + Big Rocks proof + House Rules + CTAs.",
          messagingIntent: [
            "High contrast: House metaphor + local coaching promise.",
            "Proof first: Big Rocks (Consistency, Pain, Protein).",
            "Commitment device: House Rules = identity shift.",
          ],
          whatClientFeels:
            "Finally, something that makes sense. 'Build a house' is something I can do.",
          talkTrack:
            "Most fitness fails because it's random. We build your body like a house: Foundation first.",
          uiCues: [
            "BUILD THE FOUNDATION chip = immediate authority.",
            "3 buttons = direct paths: plan / why / book.",
            "House Rules = micro-ritual (buy-in).",
          ],
          keyCopy: {
            headline: "Build a body you can live in.",
            ctas: ["See the Plan", "Why this works", "Book Now"],
          },
          successCriteria: [
            "Hero communicates metaphor + promise in <5 seconds.",
            "Proof cards read cleanly on mobile.",
            "House Rules button toggles state clearly.",
          ],
          actions: [
            { type: "jump", label: "View Home", target: "home" },
            { type: "modal", label: "Test Booking", target: "booking" },
          ],
        },

        {
          id: "plan",
          file: "src/views/PlanView.jsx",
          lens: ["flow"],
          houseStage: "Foundation",
          scottIntent:
            "Show them the 'Build Sequence'. Simple blocks, no gadgets.",
          renders: "Plan structure + weekly logic + session blocks.",
          messagingIntent: [
            "Reduce overwhelm: small blocks, repeatable weekly rhythm.",
            "Clarity > intensity: show structure not science.",
            "Signals drive decisions: plan changes based on constraints.",
          ],
          whatClientFeels: "I can do this even if life gets chaotic.",
          talkTrack:
            "The plan is designed to survive real life — we optimize consistency before intensity.",
          uiCues: ["Short blocks", "clear week structure", "minimal choice"],
          successCriteria: [
            "Week structure is scannable.",
            "Sessions feel doable (15-min blocks).",
          ],
          actions: [{ type: "jump", label: "View Plan", target: "plan" }],
        },

        {
          id: "results",
          file: "src/views/ResultsView.jsx",
          lens: ["proof"],
          houseStage: "Structure",
          scottIntent:
            "Pain-aware tracking. Show that we measure what matters.",
          renders: "Outcomes + evidence + 'what changed' story.",
          messagingIntent: [
            "Proof of progress: not vanity metrics — functional wins.",
            "Make it tangible: what improved since check-in.",
            "Reinforce trust: signals interpreted by coach.",
          ],
          whatClientFeels:
            "This is tracked and real — someone is paying attention.",
          talkTrack:
            "We measure what matters so your training stays honest and sustainable.",
          uiCues: ["Verified language", "before/after framing", "signal bars"],
          actions: [{ type: "jump", label: "View Results", target: "results" }],
        },

        {
          id: "client",
          file: "src/views/ClientView.jsx",
          lens: ["proof", "flow"],
          houseStage: "Finish Work",
          scottIntent:
            "Local/Remote coaching connection. This is where I talk to you.",
          renders: "1:1 coaching portal, weekly notes, clickable timeline.",
          messagingIntent: [
            "Premium differentiator: private weekly coach recording.",
            "Narrative arc: Awareness → Calibration → Optimization → Consolidation.",
            "Control: client sees the plan, the why, and the next move.",
          ],
          whatClientFeels: "I’m not alone. This is guided. This is personal.",
          talkTrack:
            "This is where coaching becomes real — weekly decisions, made with you, not for you.",
          uiCues: [
            "Coach note video is the centerpiece.",
            "Primary focus card = one lever.",
            "CTA changes by week (past/current/future).",
          ],
          actions: [
            { type: "jump", label: "View Client Portal", target: "client" },
          ],
        },
      ],
    },

    {
      id: "components",
      label: "Components (UI Building Blocks)",
      description: "Reusable patterns that communicate brand + clarity.",
      steps: [
        {
          id: "whyModal",
          file: "src/components/WhyThisWorksModal.jsx",
          lens: ["frame", "proof"],
          houseStage: "Clear Land",
          scottIntent: "The sales argument. Old Way (Noise) vs Simpler Way.",
          renders: "Operator Lens: contrast story + proof + CTA.",
          messagingIntent: [
            "Break analyst frame: simple visual explanation.",
            "Own the frame: 'most fitness fails because…'",
            "Fast comprehension: Old way vs Simpler way.",
          ],
          whatClientFeels: "I get it instantly. This is different.",
          talkTrack:
            "This is the 20-second story: old model fights your life; our system works with it.",
          uiCues: ["Split-lane visual", "proof cards", "single CTA"],
          actions: [{ type: "modal", label: "Test Modal", target: "why" }],
        },
        {
          id: "buttons",
          file: "src/components/Button.jsx",
          lens: ["flow"],
          houseStage: "Maintenance",
          scottIntent: "Clear next action. Never guess.",
          renders: "Primary vs secondary actions.",
          messagingIntent: [
            "Primary = decisive action (Book / Accept).",
            "Secondary = curiosity path (Why).",
          ],
          whatClientFeels: "I always know what the main move is.",
          talkTrack: "The UI always gives you one obvious next step.",
          uiCues: ["Green glow on primary", "quiet secondary"],
        },
      ],
    },

    {
      id: "data",
      label: "Data Files (Logic)",
      description: "Where messaging becomes editable content.",
      steps: [
        {
          id: "clientDemo",
          file: "src/data/clientDemo.js",
          lens: ["proof", "flow"],
          houseStage: "Foundation",
          scottIntent: "The engine that makes the coaching real.",
          renders: "Weekly story arc content + CTA language + signals.",
          messagingIntent: [
            "Consistency of voice across weeks.",
            "Rakim arc = structured narrative rhythm.",
            "Scales to real clients without rewriting components.",
          ],
          whatClientFeels:
            "This is personal, but also systematic — like a real program.",
          talkTrack:
            "Everything you see here is data-driven — we can preload your entire coaching system per client.",
          uiCues: ["Week taglines", "context lines", "CTA mode logic"],
        },
      ],
    },

    {
      id: "styles",
      label: "Styles (Brand System)",
      description: "Why it feels premium: tokens, glass, signal colors.",
      steps: [
        {
          id: "styles",
          file: "src/styles.css",
          lens: ["frame", "proof"],
          houseStage: "Finish Work",
          scottIntent: "The fit and finish that creates trust.",
          renders: "Design tokens + glass system + brand glow.",
          messagingIntent: [
            "Calm authority: dark mode, minimal noise.",
            "Signals pop: green=go, amber=constraint.",
            "System vibe: monospace + status indicators.",
          ],
          whatClientFeels:
            "This feels like an OS, not a generic training site.",
          talkTrack:
            "The visual system reinforces the promise: signals → decisions → progress.",
          uiCues: ["--brand", "--muted", "glass cards", "signal bars"],
        },
      ],
    },
  ],
};
